import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import { SPECIES, type SpeciesId } from "../sim/species";

/**
 * Procedural adult skeletons, built once per species. Every vertex carries a
 * `birthLength` = normalized height from the base (0 at ground, 1 at the crown),
 * so the plant shader can sweep a growth front up it and unfurl the plant
 * base-to-tip (acropetal). Leaves are placed by phyllotaxis (golden angle
 * 137.5°) — the single detail that most separates "botanical" from "CG".
 *
 * Each mesh is built at adult size; the shader's per-instance growth front does
 * all the growing (the instance matrix carries only position + yaw, scale 1).
 */
type V3 = [number, number, number];
const GOLDEN = Math.PI * (3 - Math.sqrt(5)); // 137.5° in radians

class Builder {
  private pos: number[] = [];
  private nrm: number[] = [];
  private col: number[] = [];
  private bl: number[] = [];
  private idx: number[] = [];
  constructor(private readonly H: number) {}

  private vert(p: V3, n: V3, c: V3): number {
    const i = this.pos.length / 3;
    this.pos.push(p[0], p[1], p[2]);
    this.nrm.push(n[0], n[1], n[2]);
    this.col.push(c[0], c[1], c[2]);
    this.bl.push(Math.min(1, Math.max(0, p[1] / this.H)));
    return i;
  }

  /** A double-sided quad p0→p1→p2→p3. */
  quad(p0: V3, p1: V3, p2: V3, p3: V3, c: V3): void {
    const n = normal(p0, p1, p2);
    const a = this.vert(p0, n, c);
    const b = this.vert(p1, n, c);
    const d = this.vert(p2, n, c);
    const e = this.vert(p3, n, c);
    this.idx.push(a, b, d, a, d, e);
  }

  /** Tapered tube along a vertical-ish path from base, `sides`-gon rings. */
  stem(base: V3, top: V3, rBase: number, rTop: number, sides: number, c: V3): void {
    const rings = 4;
    let prev: number[] = [];
    for (let k = 0; k <= rings; k++) {
      const t = k / rings;
      const cx = base[0] + (top[0] - base[0]) * t;
      const cy = base[1] + (top[1] - base[1]) * t;
      const cz = base[2] + (top[2] - base[2]) * t;
      const r = rBase + (rTop - rBase) * t;
      const ring: number[] = [];
      for (let s = 0; s < sides; s++) {
        const a = (s / sides) * Math.PI * 2;
        const nx = Math.cos(a);
        const nz = Math.sin(a);
        ring.push(this.vert([cx + nx * r, cy, cz + nz * r], [nx, 0.2, nz], c));
      }
      if (k > 0) {
        for (let s = 0; s < sides; s++) {
          const s1 = (s + 1) % sides;
          this.idx.push(prev[s]!, prev[s1]!, ring[s1]!, prev[s]!, ring[s1]!, ring[s]!);
        }
      }
      prev = ring;
    }
  }

  /** A leaf/petal card at `attach`, pointing along `dir` (outward+up), given size. */
  leaf(attach: V3, dir: V3, size: number, width: number, c: V3): void {
    const up: V3 = [0, 1, 0];
    let side = cross(dir, up);
    side = norm(side);
    if (!isFinite(side[0])) side = [1, 0, 0];
    const tip: V3 = [attach[0] + dir[0] * size, attach[1] + dir[1] * size, attach[2] + dir[2] * size];
    const mid: V3 = [attach[0] + dir[0] * size * 0.5, attach[1] + dir[1] * size * 0.5, attach[2] + dir[2] * size * 0.5];
    const l: V3 = [mid[0] - side[0] * width, mid[1] - side[1] * width, mid[2] - side[2] * width];
    const r: V3 = [mid[0] + side[0] * width, mid[1] + side[1] * width, mid[2] + side[2] * width];
    this.quad(attach, l, tip, r, c);
  }

  toMesh(scene: Scene, name: string): Mesh {
    const data = new VertexData();
    data.positions = this.pos;
    data.indices = this.idx;
    data.normals = this.nrm;
    data.colors = expandColors(this.col);
    const mesh = new Mesh(name, scene);
    data.applyToMesh(mesh);
    mesh.setVerticesData("birthLength", new Float32Array(this.bl), false, 1);
    return mesh;
  }
}

function expandColors(rgb: number[]): number[] {
  const out: number[] = [];
  for (let i = 0; i < rgb.length; i += 3) out.push(rgb[i]!, rgb[i + 1]!, rgb[i + 2]!, 1);
  return out;
}

// --- vector helpers ---------------------------------------------------------
function normal(a: V3, b: V3, c: V3): V3 {
  const u: V3 = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const v: V3 = [c[0] - a[0], c[1] - a[1], c[2] - a[2]];
  return norm(cross(u, v));
}
function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function norm(a: V3): V3 {
  const l = Math.hypot(a[0], a[1], a[2]) || 1;
  return [a[0] / l, a[1] / l, a[2] / l];
}

// --- per-species generators -------------------------------------------------
export function buildSkeleton(scene: Scene, id: SpeciesId): Mesh {
  const [cr, cg, cb] = SPECIES[id].color;
  switch (id) {
    case "grass":
      return grass(scene, [cr, cg, cb]);
    case "heather":
      return heather(scene, [cr, cg, cb]);
    case "reed":
      return reed(scene, [cr, cg, cb]);
    case "rowan":
      return rowan(scene, [cr, cg, cb]);
  }
}

function grass(scene: Scene, c: V3): Mesh {
  const H = 0.45;
  const b = new Builder(H);
  const blades = 7;
  for (let i = 0; i < blades; i++) {
    const a = i * GOLDEN;
    const lean = 0.35 + (i % 3) * 0.08;
    const dir = norm([Math.cos(a) * lean, 1, Math.sin(a) * lean]);
    const shade: V3 = [c[0] * (0.8 + 0.2 * (i % 2)), c[1] * (0.8 + 0.25 * (i % 2)), c[2] * 0.9];
    b.leaf([0, 0.02, 0], dir, H * (0.85 + 0.3 * ((i % 3) / 3)), 0.03, shade);
  }
  return b.toMesh(scene, "grassSrc");
}

function reed(scene: Scene, c: V3): Mesh {
  const H = 1.7;
  const b = new Builder(H);
  const stems = 4;
  for (let i = 0; i < stems; i++) {
    const a = i * GOLDEN;
    const bx = Math.cos(a) * 0.08;
    const bz = Math.sin(a) * 0.08;
    const lean = 0.12;
    const top: V3 = [bx + Math.cos(a) * lean, H * (0.82 + 0.12 * (i % 2)), bz + Math.sin(a) * lean];
    b.stem([bx, 0, bz], top, 0.035, 0.01, 4, [c[0] * 0.8, c[1] * 0.85, c[2] * 0.7]);
    // brown seed head at the tip
    b.leaf(top, norm([top[0] - bx, 0.6, top[2] - bz]), 0.22, 0.04, [0.5, 0.38, 0.2]);
  }
  return b.toMesh(scene, "reedSrc");
}

function heather(scene: Scene, c: V3): Mesh {
  const H = 0.55;
  const b = new Builder(H);
  const twigs = 11;
  const twigCol: V3 = [0.28, 0.34, 0.2];
  for (let i = 0; i < twigs; i++) {
    const a = i * GOLDEN;
    const spread = 0.28 + 0.12 * ((i % 4) / 4);
    const h = H * (0.5 + 0.45 * ((i * 7) % 5) / 5);
    const tip: V3 = [Math.cos(a) * spread, h, Math.sin(a) * spread];
    b.stem([Math.cos(a) * 0.05, 0.02, Math.sin(a) * 0.05], tip, 0.012, 0.005, 3, twigCol);
    // magenta flower cluster: a few small crossed petals at the tip
    const dir = norm([Math.cos(a) * 0.4, 1, Math.sin(a) * 0.4]);
    b.leaf(tip, dir, 0.09, 0.05, c);
    b.leaf(tip, norm([dir[2], dir[1], -dir[0]]), 0.08, 0.045, c);
  }
  return b.toMesh(scene, "heatherSrc");
}

function rowan(scene: Scene, c: V3): Mesh {
  const H = 7.2; // a proper canopy tree now
  const b = new Builder(H);
  const bark: V3 = [0.36, 0.26, 0.17];
  const barkHi: V3 = [0.44, 0.33, 0.22];
  const berry: V3 = [0.86, 0.34, 0.12]; // rowan's signature orange berries

  // Trunk, forking into a few main limbs at the crown break.
  const forkH = H * 0.5;
  b.stem([0, 0, 0], [0, forkH, 0], 0.24, 0.11, 7, bark);

  // Main limbs fork up and out; each carries a couple of sub-branches. Collect
  // the sub-branch tips as anchor points for the crown foliage.
  const limbs = 5;
  const tips: V3[] = [];
  for (let i = 0; i < limbs; i++) {
    const a = i * GOLDEN;
    const reach = 1.5 + 0.5 * (i % 2);
    const limbTop: V3 = [Math.cos(a) * reach, H * (0.82 + 0.08 * (i % 3)), Math.sin(a) * reach];
    b.stem([Math.cos(a) * 0.06, forkH * (0.9 + 0.05 * (i % 2)), Math.sin(a) * 0.06], limbTop, 0.1, 0.04, 5, barkHi);
    tips.push(limbTop);
    // two sub-branches splitting off each limb
    for (let k = 0; k < 2; k++) {
      const aa = a + (k === 0 ? 0.6 : -0.6);
      const sub: V3 = [limbTop[0] * 0.6 + Math.cos(aa) * 0.9, limbTop[1] + 0.5 + 0.3 * k, limbTop[2] * 0.6 + Math.sin(aa) * 0.9];
      b.stem([limbTop[0] * 0.7, limbTop[1] - 0.3, limbTop[2] * 0.7], sub, 0.04, 0.018, 4, barkHi);
      tips.push(sub);
    }
  }

  // Foliage: a rounded, slightly drooping crown of phyllotaxis leaf cards around
  // the branch tips, denser and larger toward the outside.
  const leaves = 84;
  for (let i = 0; i < leaves; i++) {
    const a = i * GOLDEN;
    const t = i / leaves;
    const base = tips[i % tips.length]!;
    const rad = 0.6 + 1.7 * Math.sqrt(1 - t * 0.5);
    const hy = H * 0.66 + (base[1] - H * 0.66) * 0.7 + Math.sin(i * 1.3) * 0.25;
    const attach: V3 = [base[0] * 0.55 + Math.cos(a) * rad, hy, base[2] * 0.55 + Math.sin(a) * rad];
    const dir = norm([Math.cos(a) * 0.9, 0.3 - 0.3 * t, Math.sin(a) * 0.9]); // outer leaves droop
    const shade = 0.72 + 0.34 * ((i * 3) % 5) / 5;
    b.leaf(attach, dir, 0.85 + 0.35 * t, 0.42, [c[0] * shade, c[1] * shade, c[2] * shade * 0.95]);
    // hang a berry cluster off every few outer leaves
    if (t > 0.35 && i % 4 === 0) {
      for (let q = 0; q < 3; q++) {
        const off: V3 = [attach[0] + (q - 1) * 0.08, attach[1] - 0.18 - 0.06 * q, attach[2] + (q - 1) * 0.05];
        b.leaf(off, [0, -1, 0], 0.11, 0.07, berry);
      }
    }
  }
  return b.toMesh(scene, "rowanSrc");
}
